/**
 * ReferralCodeDialog — creates or edits one referral code.
 *
 * The code and its commission are immutable once created (the API has no way to
 * restate an existing code's rate without invalidating the tracking already
 * attributed to it), so edit mode only exposes the description.
 */
import { useEffect, useState, type FormEvent } from 'react';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import type { AffiliateAuth } from '@wlb/shared/api/affiliate/affiliateApi';
import type { AffiliateReferralCode } from '@wlb/shared/api/affiliate/affiliateTypes';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { generateAffiliateReferralCode } from '@wlb/shared/utils/affiliateReferralCode';
import { Button } from '../../components/ui/Button';
import { FormError } from '../../components/ui/FormError';
import { Modal } from '../../components/ui/Modal';
import { RefreshIcon } from '../../components/ui/icons';
import '../../components/layout/authFlow.css';
import './affiliatePortal.css';

const MIN_CODE_LENGTH = 3;

interface ReferralCodeDialogProps {
  affiliateUserId: number;
  /** Seeds the generated code's suffix. */
  affiliateName: string;
  auth: AffiliateAuth;
  /** `null` opens the dialog in create mode. */
  code: AffiliateReferralCode | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ReferralCodeDialog({
  affiliateUserId,
  affiliateName,
  auth,
  code,
  onClose,
  onSaved,
}: ReferralCodeDialogProps) {
  const { t } = useTranslation();
  const isEdit = code != null;

  const [referralCode, setReferralCode] = useState(code?.code ?? '');
  const [description, setDescription] = useState(code?.description ?? '');
  const [commission, setCommission] = useState(
    code ? String(code.commissionPerDownload) : '',
  );
  const [codeError, setCodeError] = useState('');
  const [commissionError, setCommissionError] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setReferralCode(code?.code ?? '');
    setDescription(code?.description ?? '');
    setCommission(code ? String(code.commissionPerDownload) : '');
    setCodeError('');
    setCommissionError('');
    setFormError('');
  }, [code]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');

    const trimmedCode = referralCode.trim();
    const parsedCommission = Number(commission);
    const nextCodeError =
      trimmedCode.length < MIN_CODE_LENGTH ? t('affiliate.validation.codeTooShort') : '';
    const nextCommissionError =
      isEdit || (commission !== '' && Number.isFinite(parsedCommission) && parsedCommission > 0)
        ? ''
        : t('affiliate.validation.commissionRequired');
    setCodeError(nextCodeError);
    setCommissionError(nextCommissionError);
    if (nextCodeError || nextCommissionError) return;

    setSaving(true);
    try {
      if (isEdit) {
        await affiliateService.updateReferralCode(
          {
            affiliateReferralCodeId: code.id,
            affiliateUserId,
            referralCode: trimmedCode,
            description: description.trim(),
          },
          auth,
        );
      } else {
        await affiliateService.createReferralCode(
          {
            affiliateUserId,
            referralCode: trimmedCode,
            description: description.trim(),
            commissionPerDownload: parsedCommission,
          },
          auth,
        );
      }
      ToastService.show({
        type: 'success',
        message: t(isEdit ? 'affiliate.referralCode.updated' : 'affiliate.referralCode.created'),
      });
      onSaved();
    } catch (error) {
      setFormError(
        getUserFacingErrorMessage(error, t('affiliate.referralCode.saveFailed')),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t(isEdit ? 'affiliate.referralCode.editTitle' : 'affiliate.referralCode.addTitle')}
      labelledBy="affiliate-code-dialog-title"
      className="affiliate-code-dialog"
      onClose={onClose}
    >
      <form className="affiliate-code-form" onSubmit={handleSubmit} noValidate>
        <div className="affiliate-code-form__body">
          <FormError variant="banner" message={formError} id="affiliate-code-error" />

          <label className="auth-flow__field">
            <span>{t('affiliate.referralCode.codeLabel')}</span>
            <span className="affiliate-code-form__code">
              <input
                className={`auth-flow__input${codeError ? ' auth-flow__input--error' : ''}`}
                type="text"
                value={referralCode}
                onChange={event => {
                  setReferralCode(event.target.value);
                  if (codeError) setCodeError('');
                }}
                placeholder={t('affiliate.referralCode.codePlaceholder')}
                readOnly={isEdit}
                aria-invalid={Boolean(codeError)}
                aria-describedby={codeError ? 'affiliate-code-code-error' : undefined}
              />
              {!isEdit && (
                <Button
                  className="affiliate-code-form__generate"
                  variant="outline"
                  size="sm"
                  disabled={saving}
                  onClick={() => {
                    setReferralCode(generateAffiliateReferralCode(affiliateName));
                    setCodeError('');
                  }}
                  aria-label={t('affiliate.referralCode.generate')}
                  title={t('affiliate.referralCode.generate')}
                >
                  <RefreshIcon size={16} />
                </Button>
              )}
            </span>
            <FormError message={codeError} id="affiliate-code-code-error" />
          </label>

          <label className="auth-flow__field">
            <span>{t('affiliate.referralCode.descriptionLabel')}</span>
            <textarea
              className="auth-flow__input affiliate-code-form__textarea"
              rows={3}
              value={description}
              onChange={event => setDescription(event.target.value)}
              placeholder={t('affiliate.referralCode.descriptionPlaceholder')}
            />
          </label>

          <label className="auth-flow__field">
            <span>{t('affiliate.referralCode.commissionLabel')}</span>
            <input
              className={`auth-flow__input affiliate-code-form__commission${commissionError ? ' auth-flow__input--error' : ''}`}
              type="number"
              inputMode="decimal"
              min="0"
              step="0.01"
              value={commission}
              onChange={event => {
                setCommission(event.target.value);
                if (commissionError) setCommissionError('');
              }}
              readOnly={isEdit}
              aria-invalid={Boolean(commissionError)}
              aria-describedby={commissionError ? 'affiliate-code-commission-error' : undefined}
            />
            <FormError message={commissionError} id="affiliate-code-commission-error" />
          </label>
        </div>

        <div className="affiliate-code-form__actions">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.buttons.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t('common.buttons.saving') : t('common.buttons.save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
